import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ForwardedRef,
  type ReactNode,
  type SyntheticEvent,
  type VideoHTMLAttributes,
} from 'react';
import type HlsType from 'hls.js';

type Engine = 'pending' | 'hlsjs' | 'native' | 'file';

/** Seconds hls.js may buffer before the first play (it always fetches at least one segment). */
const PREPLAY_BUFFER_SECONDS = 1;

export interface HlsVideoProps extends Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src' | 'children'> {
  /** HLS master playlist. When absent the element plays `fallbackSrc` exactly as before. */
  hlsUrl?: string | null;
  /** The original file (session URL / step media URL). Always the safety net. */
  fallbackSrc?: string;
  /** Extra children (e.g. a <track> for captions). */
  children?: ReactNode;
}

/**
 * Copied from frontend/ccms_resource_centre/src/components/player/HlsVideo.tsx (keep the two in step).
 *
 * Drop-in <video> that streams HLS when it can and falls back to the original
 * file whenever anything goes wrong.
 *
 *  1. No `hlsUrl`: renders `<video src={fallbackSrc}>`, identical to the old player.
 *  2. hls.js (lazy loaded) when `Hls.isSupported()`.
 *  3. Otherwise native HLS when the browser can play `application/vnd.apple.mpegurl` (Safari, iOS).
 *  4. Otherwise the original file.
 *
 * A fatal hls.js error (after one `recoverMediaError` attempt for media
 * errors) or a native playback error switches to `fallbackSrc` at the same
 * position, keeping the paused / playing state. The element itself never
 * changes, so refs, telemetry and resume keep working. No `crossOrigin` is
 * set: legacy sources must keep playing without CORS.
 *
 * A new `hlsUrl` is only picked up before playback starts, so a late media
 * lookup or status poll never swaps the source under a viewer.
 *
 * Like `preload="metadata"` on a file, hls.js only buffers about one segment
 * up front (enough for the duration, first frame and a resume seek) and opens
 * up to its normal buffer on the first play, unless `preload="auto"` or
 * `autoPlay` is set. Saves mobile data.
 */
export const HlsVideo = forwardRef(function HlsVideo(
  { hlsUrl: hlsUrlProp, fallbackSrc, children, onLoadedMetadata, onError, onPlay, ...rest }: HlsVideoProps,
  forwardedRef: ForwardedRef<HTMLVideoElement>,
) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<HlsType | null>(null);
  const startedRef = useRef(false);
  const [hlsUrl, setHlsUrl] = useState<string | null>(hlsUrlProp ?? null);
  useEffect(() => {
    if (!startedRef.current) setHlsUrl(hlsUrlProp ?? null);
  }, [hlsUrlProp]);
  // Position + play state to restore after a fallback reload.
  const handoffRef = useRef<{ time: number; play: boolean } | null>(null);
  // The hlsUrl that failed; a different URL gets a fresh attempt.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const useHls = !!hlsUrl && failedUrl !== hlsUrl;
  const [engine, setEngine] = useState<Engine>(() => (useHls ? 'pending' : 'file'));

  const setRefs = useCallback(
    (node: HTMLVideoElement | null) => {
      videoRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  const destroyHls = () => {
    const hls = hlsRef.current;
    hlsRef.current = null;
    if (hls) {
      try {
        hls.destroy();
      } catch {
        /* already gone */
      }
    }
  };

  // Switch to the original file at the current position.
  const fallBack = useCallback(
    (url: string) => {
      const el = videoRef.current;
      handoffRef.current = el
        ? { time: el.currentTime || 0, play: !el.paused && !el.ended }
        : null;
      destroyHls();
      setFailedUrl(url);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!useHls || !hlsUrl || !video) {
      setEngine('file');
      return;
    }

    let cancelled = false;
    let detachListeners: (() => void) | null = null;
    setEngine('pending');

    import('hls.js')
      .then(({ default: Hls }) => {
        if (cancelled) return;
        if (Hls.isSupported()) {
          // Play may already have been pressed while hls.js was loading.
          const eager = rest.preload === 'auto' || !!rest.autoPlay || !video.paused;
          const hls = new Hls({
            capLevelToPlayerSize: true,
            ...(eager
              ? {}
              : { maxBufferLength: PREPLAY_BUFFER_SECONDS, maxMaxBufferLength: PREPLAY_BUFFER_SECONDS }),
          });
          hlsRef.current = hls;
          if (!eager) {
            const openBuffer = () => {
              hls.config.maxBufferLength = Hls.DefaultConfig.maxBufferLength;
              hls.config.maxMaxBufferLength = Hls.DefaultConfig.maxMaxBufferLength;
              video.removeEventListener('play', openBuffer);
            };
            video.addEventListener('play', openBuffer);
            detachListeners = () => video.removeEventListener('play', openBuffer);
          }
          let recovered = false;
          hls.on(Hls.Events.ERROR, (_event, data) => {
            if (!data.fatal || hlsRef.current !== hls) return;
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recovered) {
              recovered = true;
              try {
                hls.recoverMediaError();
                return;
              } catch {
                /* fall through to the original file */
              }
            }
            fallBack(hlsUrl);
          });
          hls.attachMedia(video);
          hls.loadSource(hlsUrl);
          setEngine('hlsjs');
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          setEngine('native');
        } else {
          fallBack(hlsUrl);
        }
      })
      .catch(() => {
        if (!cancelled) fallBack(hlsUrl);
      });

    return () => {
      cancelled = true;
      detachListeners?.();
      destroyHls();
    };
    // `preload` / `autoPlay` are read once per source on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useHls, hlsUrl, fallBack]);

  const handleLoadedMetadata = (e: SyntheticEvent<HTMLVideoElement>) => {
    const handoff = handoffRef.current;
    if (handoff && engine === 'file') {
      handoffRef.current = null;
      const el = e.currentTarget;
      if (handoff.time > 0.5) {
        try {
          el.currentTime = handoff.time;
        } catch {
          /* ignore */
        }
      }
      if (handoff.play) el.play().catch(() => { /* autoplay blocked: stays paused */ });
    }
    onLoadedMetadata?.(e);
  };

  const handlePlay = (e: SyntheticEvent<HTMLVideoElement>) => {
    startedRef.current = true;
    onPlay?.(e);
  };

  const handleError = (e: SyntheticEvent<HTMLVideoElement>) => {
    if (engine === 'native' && hlsUrl) {
      fallBack(hlsUrl);
      return;
    }
    onError?.(e);
  };

  const src =
    engine === 'file' ? fallbackSrc : engine === 'native' ? (hlsUrl ?? undefined) : undefined;

  return (
    <video
      ref={setRefs}
      {...rest}
      src={src}
      onLoadedMetadata={handleLoadedMetadata}
      onPlay={handlePlay}
      onError={handleError}
    >
      {children}
    </video>
  );
});
